import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  QrCode2 as QrCodeIcon,
  Analytics as AnalyticsIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import qrcodeService, { QRCode } from '../services/qrcodeService';

const QRCodeList: React.FC = () => {
  const navigate = useNavigate();

  const [qrcodes, setQrcodes] = useState<QRCode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [qrcodeToDelete, setQrcodeToDelete] = useState<QRCode | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch QR codes
  const fetchQRCodes = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await qrcodeService.getAll({
        page: page + 1,
        limit: rowsPerPage,
        search: searchQuery,
        sortBy: 'created_at',
        sortOrder: 'DESC',
      });

      setQrcodes(response.data);
      setTotalCount(response.pagination.total);
    } catch (err: any) {
      setError(err.error || 'Failed to load QR codes');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchQRCodes();
  }, [page, rowsPerPage]);

  // Handle search
  const handleSearch = () => {
    setPage(0);
    fetchQRCodes();
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Handle pagination
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle delete
  const handleDeleteClick = (qrcode: QRCode) => {
    setQrcodeToDelete(qrcode);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!qrcodeToDelete) return;

    try {
      setIsDeleting(true);
      await qrcodeService.delete(qrcodeToDelete.id);
      setDeleteDialogOpen(false);
      setQrcodeToDelete(null);
      fetchQRCodes(); // Refresh list
    } catch (err: any) {
      setError(err.error || 'Failed to delete QR code');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setQrcodeToDelete(null);
  };

  // Handle download
  const handleDownload = async (id: number) => {
    try {
      await qrcodeService.downloadImage(id, 'png');
    } catch (err) {
      setError('Failed to download QR code');
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              onClick={() => navigate('/dashboard')}
              sx={{ color: 'primary.main' }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" component="h1">
              Mes codes QR
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/qrcodes/create')}
          >
            Créer nouveau
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Search */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Rechercher des codes QR par nom, description ou URL..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={handleSearchKeyPress}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Button onClick={handleSearch}>Rechercher</Button>
                </InputAdornment>
              ),
            }}
          />
        </Paper>

        {/* Table */}
        <TableContainer component={Paper}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : qrcodes.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <QrCodeIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Aucun code QR trouvé
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {searchQuery
                  ? 'Essayez d\'ajuster vos critères de recherche'
                  : 'Créez votre premier code QR pour commencer'}
              </Typography>
              {!searchQuery && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/qrcodes/create')}
                >
                  Créer un code QR
                </Button>
              )}
            </Box>
          ) : (
            <>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Nom</TableCell>
                    <TableCell>URL de destination</TableCell>
                    <TableCell>Code court</TableCell>
                    <TableCell align="center">Scans</TableCell>
                    <TableCell>Créé le</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {qrcodes.map((qrcode) => (
                    <TableRow key={qrcode.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {qrcode.name}
                        </Typography>
                        {qrcode.description && (
                          <Typography variant="caption" color="text.secondary">
                            {qrcode.description.length > 50
                              ? `${qrcode.description.substring(0, 50)}...`
                              : qrcode.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {qrcode.destination_url}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={qrcode.shortcode} size="small" />
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight="medium">
                          {qrcode.scan_count}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatDate(qrcode.created_at)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Aperçu du code QR">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/qrcodes/${qrcode.id}`)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Voir les statistiques">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/qrcodes/${qrcode.id}`)}
                          >
                            <AnalyticsIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Modifier">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/qrcodes/${qrcode.id}/edit`)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Télécharger">
                          <IconButton size="small" onClick={() => handleDownload(qrcode.id)}>
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Supprimer">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteClick(qrcode)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          )}
        </TableContainer>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
          <DialogTitle>Supprimer le code QR</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Êtes-vous sûr de vouloir supprimer "{qrcodeToDelete?.name}" ? Cette action est irréversible.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel} disabled={isDeleting}>
              Annuler
            </Button>
            <Button onClick={handleDeleteConfirm} color="error" disabled={isDeleting}>
              {isDeleting ? <CircularProgress size={20} /> : 'Supprimer'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default QRCodeList;
